import React from 'react';
import { Send, PenLine, Hourglass, XCircle, BadgeCheck, Wallet, Info, Users, ChevronDown, ChevronUp, RefreshCw, AlertTriangle } from 'lucide-react';
import type { EstadoResultadoMes, PessoaResultadoMes } from './useResultadoMes';
import type { Contagem } from './resultadoMes';
import { rotuloFaixaMes } from '../../../src/lib/bonificacaoParceiroFolha';

// Resultado do mês da barra Meu Trabalho: a jornada do mês (#6426) — termos em
// assinatura, assinados e, dos assinados, quantos estão em validação (na fila da
// analista), com pendência (já analisados, esperando o cliente), reprovados e
// aprovados — e a comissão garantida, sempre visível: o vendedor abre o CRM e já sabe
// como está no mês. As etapas depois de "assinados" somam os assinados ("=" e "+" na tela). Segue
// o filtro de responsáveis. Em tela larga fica no centro da linha do título; em tela
// estreita, numa linha própria logo abaixo; no celular, só quando a barra está aberta.

const fmtKwh = (kwh: number) => `${Math.round(kwh).toLocaleString('pt-BR')} kWh`;
const fmtRs = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type ChaveEtapa = 'emAssinatura' | 'assinados' | 'validacao' | 'reprovados' | 'aprovados';

interface DefEtapa {
    key: ChaveEtapa;
    label: string;
    curto: string;
    /** Sinal antes da etapa: as três últimas repartem os assinados. */
    antes?: '=' | '+';
    icon: React.ElementType;
    tone: string;
    bg: string;
    ajuda: (mes: string) => string;
}

const ETAPAS: DefEtapa[] = [
    {
        key: 'emAssinatura', label: 'Termos em assinatura', curto: 'Em assinatura', icon: Send,
        tone: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20',
        ajuda: mes => `Termos enviados em ${mes} que ainda esperam o cliente assinar.`,
    },
    {
        key: 'assinados', label: 'Termos assinados', curto: 'Assinados', icon: PenLine,
        tone: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20',
        ajuda: mes => `Termos assinados em ${mes}. Validação + pendência + reprovados + aprovados somam este número.`,
    },
    {
        key: 'validacao', label: 'Validação de dados', curto: 'Validação', antes: '=', icon: Hourglass,
        tone: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-900/20',
        ajuda: mes => `Dos assinados em ${mes}, os que estão na fila da validação de dados, esperando a analista. Quem já foi analisado e espera o cliente aparece em "com pendência".`,
    },
    {
        key: 'reprovados', label: 'Reprovados', curto: 'Reprovados', antes: '+', icon: XCircle,
        tone: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20',
        ajuda: mes => `Dos assinados em ${mes}, os reprovados na validação de dados.`,
    },
    {
        key: 'aprovados', label: 'Total aprovados', curto: 'Aprovados', antes: '+', icon: BadgeCheck,
        tone: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20',
        ajuda: mes => `Dos assinados em ${mes}, os já aprovados na validação de dados.`,
    },
];

const Esqueleto: React.FC<{ largura?: string }> = ({ largura = 'w-20' }) => (
    <span className={`inline-block h-3.5 ${largura} rounded bg-gray-100 dark:bg-gray-700 animate-pulse align-middle`} />
);

const ValorContagem: React.FC<{ c: Contagem }> = ({ c }) => (
    <span className="tabular-nums whitespace-nowrap">
        <span className="font-bold text-gray-800 dark:text-gray-100">{c.qtd}</span>
        <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
        <span className="font-semibold text-gray-500 dark:text-gray-400">{fmtKwh(c.kwh)}</span>
    </span>
);

const plural = (n: number, um: string, varios: string) => (n === 1 ? um : varios);

/** Para o parceiro o nome é "bonificação" (regra de 26/10/2026); para o resto, "comissão". */
const soParceiros = (estado: EstadoResultadoMes) => {
    const comPolitica = estado.pessoas.filter(p => p.politica);
    return comPolitica.length > 0 && comPolitica.every(p => p.politica === 'parceiro');
};

/** Explica o número da comissão — o que entra, o que fica de fora e o que falta para entrar. */
function notasComissao(estado: EstadoResultadoMes): string[] {
    const mes = estado.janela.inicio.toLocaleDateString('pt-BR', { month: 'long' });
    const comPolitica = estado.pessoas.filter(p => p.politica);
    if (comPolitica.length === 0) return ['Ninguém deste filtro recebe comissão de vendedor pela Folha de Pagamento.'];
    const parceiro = soParceiros(estado);

    const notas = [
        `${parceiro ? 'Bonificação' : 'Comissão'} das negociações com termo aprovado em ${mes}, pelas regras da Folha de Pagamento, como se o mês fechasse hoje.`,
        'Não entra salário nem bônus que só se decide no fechamento do mês.',
    ];
    for (const p of comPolitica) {
        const f = p.comissao?.faixaParceiro;
        if (!f) continue;
        const quem = comPolitica.length > 1 ? `${p.nome}: ` : '';
        notas.push(`${quem}aceites a partir de 26/10/2026 seguem a regra nova — ${rotuloFaixaMes(f.faixa, f.pct, f.kwh)} (somando a equipe). O garantido é o Pix I (30%), pago no dia 10 do mês seguinte.`);
    }
    const deOutroMes = comPolitica.reduce((s, p) => s + p.aprovadasDeOutroMes, 0);
    if (deOutroMes > 0) notas.push(`Inclui ${deOutroMes} ${plural(deOutroMes, 'aprovação', 'aprovações')} de termo assinado antes de ${mes}, que não ${plural(deOutroMes, 'aparece', 'aparecem')} em "Total aprovados" (lá só entram os assinados em ${mes}).`);
    if (comPolitica.some(p => p.politica === 'interno_v1')) notas.push('Closer e BDR: o percentual sobe conforme a meta do mês avança.');
    if (estado.total.comissao == null) notas.push('Não foi possível ler a tarifa ou a meta de alguém do filtro, então o total não aparece.');

    const semPlano = comPolitica.reduce((s, p) => s + (p.comissao?.semPlano || 0), 0);
    const grupoA = comPolitica.reduce((s, p) => s + (p.comissao?.grupoA || 0), 0);
    const aposFatura = comPolitica.reduce((s, p) => s + (p.comissao?.aposPrimeiraFatura || 0), 0);
    const indicadasPosVenda = comPolitica.reduce((s, p) => s + (p.comissao?.indicacoesPosVenda || 0), 0);
    const foraDaRegra = comPolitica.reduce((s, p) => s + (p.comissao?.foraDaRegra || 0), 0);
    if (semPlano > 0) notas.push(`${semPlano} aprovada${semPlano > 1 ? 's' : ''} sem plano definido (desconto da UC) fica${semPlano > 1 ? 'm' : ''} de fora até o plano ser preenchido.`);
    if (grupoA > 0) notas.push(`${grupoA} aprovada${grupoA > 1 ? 's' : ''} do Grupo A: fora deste valor.`);
    if (foraDaRegra > 0) notas.push(`${foraDaRegra} aprovada${foraDaRegra > 1 ? 's' : ''} fora da regra da bonificação (ex.: abaixo de 500 kWh, desconto fora da regra ou acima do teto do segmento): não ${plural(foraDaRegra, 'entra', 'entram')} na conta.`);
    if (aposFatura > 0) notas.push(`Parceiro: mais ${fmtRs(aposFatura)} de bonificação depois da 1ª fatura paga do cliente (Pix II).`);
    if (indicadasPosVenda > 0) notas.push(`${indicadasPosVenda} venda${indicadasPosVenda > 1 ? 's' : ''} indicada${indicadasPosVenda > 1 ? 's' : ''} pelo pós-venda: a comissão vale 120% e é dividida meio a meio com o pós-venda (o valor acima já é a sua metade).`);
    if (comPolitica.some(p => p.comissao?.slaPendente)) notas.push('Vendedor externo: metade da ativação fica retida até cumprir o SLA do mês.');
    return notas;
}

function motivoSemComissao(p: PessoaResultadoMes): string {
    if (!p.politica) return 'Sem comissão de vendedor na Folha (gestão etc.). Venda de vendedor de parceiro gera bonificação para o parceiro.';
    return 'Não foi possível ler a tarifa ou a meta desta pessoa';
}

const TabelaPorVendedor: React.FC<{ estado: EstadoResultadoMes }> = ({ estado }) => {
    // Perdido depois de assinar é raro; a coluna só aparece quando existe, para a linha fechar.
    const comSaidos = estado.total.saidos.qtd > 0;
    // Pendência (já analisado, esperando o cliente): coluna logo depois de Validação, só quando existe.
    const comPendencia = estado.total.pendencia.qtd > 0;
    return (
        <div className="mt-2.5 overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
            <table className="w-full text-xs">
                <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/40 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        <th className="text-left font-semibold px-3 py-2">Vendedor</th>
                        {ETAPAS.map(e => (
                            <React.Fragment key={e.key}>
                                <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">{e.curto}</th>
                                {e.key === 'validacao' && comPendencia && <th className="text-right font-semibold px-3 py-2 whitespace-nowrap" title="Já analisados pela validação de dados, esperando documento, garantia ou confirmação do cliente">Pendência</th>}
                            </React.Fragment>
                        ))}
                        {comSaidos && <th className="text-right font-semibold px-3 py-2 whitespace-nowrap" title="Assinados no mês e perdidos depois">Perdidos</th>}
                        <th className="text-right font-semibold px-3 py-2 whitespace-nowrap">{soParceiros(estado) ? 'Bonificação' : 'Comissão'} garantida</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {estado.pessoas.map(p => (
                        <tr key={p.responsavelId} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20">
                            <td className="px-3 py-1.5 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">{p.nome}</td>
                            {ETAPAS.map(e => (
                                <React.Fragment key={e.key}>
                                    <td className="px-3 py-1.5 text-right">
                                        {p[e.key].qtd > 0 ? <ValorContagem c={p[e.key]} /> : <span className="text-gray-300 dark:text-gray-600">0</span>}
                                    </td>
                                    {e.key === 'validacao' && comPendencia && (
                                        <td className="px-3 py-1.5 text-right">
                                            {p.pendencia.qtd > 0 ? <ValorContagem c={p.pendencia} /> : <span className="text-gray-300 dark:text-gray-600">0</span>}
                                        </td>
                                    )}
                                </React.Fragment>
                            ))}
                            {comSaidos && (
                                <td className="px-3 py-1.5 text-right">
                                    {p.saidos.qtd > 0 ? <ValorContagem c={p.saidos} /> : <span className="text-gray-300 dark:text-gray-600">0</span>}
                                </td>
                            )}
                            <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                {p.comissao ? (
                                    <span className="font-bold text-primary dark:text-lime-400 tabular-nums">
                                        {fmtRs(p.comissao.valor)}
                                        {p.comissao.semPlano > 0 && (
                                            <span className="ml-1.5 font-semibold text-amber-600 dark:text-amber-400" title="Aprovadas sem plano definido: ficam de fora até o plano ser preenchido">
                                                · {p.comissao.semPlano} sem plano
                                            </span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="text-gray-300 dark:text-gray-600 cursor-help" title={motivoSemComissao(p)}>—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="bg-gray-50/70 dark:bg-gray-700/30 font-bold">
                        <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300">Total</td>
                        {ETAPAS.map(e => (
                            <React.Fragment key={e.key}>
                                <td className="px-3 py-1.5 text-right"><ValorContagem c={estado.total[e.key]} /></td>
                                {e.key === 'validacao' && comPendencia && <td className="px-3 py-1.5 text-right"><ValorContagem c={estado.total.pendencia} /></td>}
                            </React.Fragment>
                        ))}
                        {comSaidos && <td className="px-3 py-1.5 text-right"><ValorContagem c={estado.total.saidos} /></td>}
                        <td className="px-3 py-1.5 text-right text-primary dark:text-lime-400 tabular-nums">
                            {estado.total.comissao != null ? fmtRs(estado.total.comissao) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

interface ResumoProps {
    estado: EstadoResultadoMes;
    /** 'linha' = no centro da linha do título (telas largas); 'bloco' = linha própria, que vira grade no celular. */
    variante: 'linha' | 'bloco';
    porVendedorAberto: boolean;
    onTogglePorVendedor: () => void;
}

/** Números do mês: a jornada (em assinatura → assinados = validação + pendência + reprovados + aprovados) e a comissão garantida. */
export const ResultadoMesResumo: React.FC<ResumoProps> = ({ estado, variante, porVendedorAberto, onTogglePorVendedor }) => {
    const linha = variante === 'linha';
    const mes = estado.janela.inicio.toLocaleDateString('pt-BR', { month: 'long' });
    const primeiraCarga = estado.atualizadoEm == null && estado.carregando;
    const semPlano = estado.pessoas.reduce((s, p) => s + (p.comissao?.semPlano || 0), 0);
    const notas = notasComissao(estado);
    const nomeGanho = soParceiros(estado) ? 'Bonificação' : 'Comissão';
    const caixaIcone = linha ? 'w-6 h-6 rounded-md' : 'w-7 h-7 rounded-lg';
    const saidos = estado.total.saidos;
    const pendencia = estado.total.pendencia;

    return (
        <div className={linha
            // Na linha do título o espaço é justo: rótulo curto até 1760px, ícone só a partir de 1920px
            ? 'flex items-center gap-2.5 min-[1760px]:gap-3.5 whitespace-nowrap'
            : 'grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2'}>
            <div className={`flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-gray-500 ${linha ? '' : 'col-span-2 sm:col-span-1'}`}>
                {/* Na linha do título, o "Resultado de" só aparece quando sobra espaço */}
                <span><span className={linha ? 'hidden min-[1760px]:inline' : ''}>Resultado de </span>{mes}</span>
                {estado.carregando && !primeiraCarga && <RefreshCw size={10} className="animate-spin" aria-label="Atualizando" />}
            </div>

            {ETAPAS.map(({ key, label, curto, antes, icon: Icon, tone, bg, ajuda }) => (
                <React.Fragment key={key}>
                    {antes && (
                        // Só onde as etapas ficam em fila; na grade do celular o sinal ocuparia uma célula
                        <span aria-hidden className="hidden sm:inline text-sm font-bold text-gray-300 dark:text-gray-600 -mx-0.5">{antes}</span>
                    )}
                    <div className="flex items-center gap-2 min-w-0 cursor-help" title={ajuda(mes)}>
                        <span className={`${caixaIcone} ${linha ? 'hidden min-[1920px]:flex' : 'flex'} shrink-0 items-center justify-center ${bg}`}>
                            <Icon size={14} className={tone} />
                        </span>
                        <div className="leading-tight min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                {linha ? (
                                    <>
                                        <span className="min-[1760px]:hidden">{curto}</span>
                                        <span className="hidden min-[1760px]:inline">{label}</span>
                                    </>
                                ) : label}
                            </p>
                            <p className="text-[13px]">{primeiraCarga ? <Esqueleto /> : <ValorContagem c={estado.total[key]} />}</p>
                        </div>
                    </div>
                    {/* Já analisados e esperando o cliente: fora da validação, mas a soma dos assinados precisa deles */}
                    {key === 'validacao' && pendencia.qtd > 0 && (
                        <div className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap cursor-help"
                            title={`Assinados em ${mes} que a validação de dados já analisou e devolveu com pendência (documento, garantia ou confirmação do cliente): ${fmtKwh(pendencia.kwh)}. Esperam o cliente e não contam na validação.`}>
                            + {pendencia.qtd} com pendência
                        </div>
                    )}
                </React.Fragment>
            ))}

            {/* Assinado e perdido depois: raro, mas sem ele a soma não fecha */}
            {saidos.qtd > 0 && (
                <div className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 dark:text-gray-500 whitespace-nowrap cursor-help"
                    title={`Assinados em ${mes} e perdidos depois — completam a soma dos assinados.`}>
                    + {saidos.qtd} {plural(saidos.qtd, 'perdido', 'perdidos')}
                </div>
            )}

            {/* Comissão garantida — explicação no hover/foco (toque no celular) */}
            <div
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                className="relative group flex items-center gap-2 min-w-0 pl-1.5 pr-3 py-1 rounded-lg bg-primary/10 dark:bg-primary/20 outline-none focus-visible:ring-2 focus-visible:ring-primary/30 cursor-help"
                aria-label={`${nomeGanho} garantida no mês`}
            >
                <span className={`${caixaIcone} shrink-0 flex items-center justify-center bg-white/70 dark:bg-gray-800/60`}>
                    <Wallet size={14} className="text-primary dark:text-lime-400" />
                </span>
                <div className="leading-tight min-w-0">
                    <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary/80 dark:text-lime-400/80 sm:whitespace-nowrap">
                        {linha ? <><span className="min-[1760px]:hidden">{nomeGanho}</span><span className="hidden min-[1760px]:inline">{nomeGanho} garantida</span></> : `${nomeGanho} garantida`}
                        <Info size={10} className="shrink-0" />
                    </p>
                    <p className={`flex items-center gap-x-1.5 text-[13px] font-extrabold text-primary dark:text-lime-400 tabular-nums ${linha ? '' : 'flex-wrap'}`}>
                        <span className="whitespace-nowrap">
                            {primeiraCarga ? <Esqueleto largura="w-16" /> : estado.total.comissao != null ? fmtRs(estado.total.comissao) : '—'}
                        </span>
                        {semPlano > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                                <AlertTriangle size={10} /> {semPlano} sem plano
                            </span>
                        )}
                    </p>
                </div>
                <div className="pointer-events-none absolute right-0 sm:left-0 sm:right-auto top-full mt-2 z-50 w-72 max-w-[80vw] whitespace-normal rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2.5 text-[11px] font-normal normal-case leading-relaxed text-white shadow-lg opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus:opacity-100">
                    <ul className="space-y-1.5">
                        {notas.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                </div>
            </div>

            {estado.pessoas.length > 1 && (
                <button
                    // Na linha do título, o clique não pode abrir/fechar a barra junto
                    onClick={(e) => { e.stopPropagation(); onTogglePorVendedor(); }}
                    title="Resultado de cada vendedor do filtro"
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${linha ? '' : 'col-span-2 sm:col-span-1 sm:ml-auto'}`}
                >
                    <Users size={12} />
                    <span className={linha ? 'hidden min-[1760px]:inline' : ''}>Por vendedor</span>
                    ({estado.pessoas.length})
                    {porVendedorAberto ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
            )}
        </div>
    );
};

/** Erro de carga e tabela por vendedor — ficam embaixo da barra nos dois formatos. */
export const ResultadoMesDetalhe: React.FC<{ estado: EstadoResultadoMes; porVendedorAberto: boolean }> = ({ estado, porVendedorAberto }) => (
    <>
        {estado.erro && <p className="mt-2 text-[11px] text-red-500 dark:text-red-400">{estado.erro}</p>}
        {porVendedorAberto && estado.pessoas.length > 1 && <TabelaPorVendedor estado={estado} />}
    </>
);
